/* ============================================================
   VEYLA | listicle F, "the reasons" : the engine

   Two jobs, and nothing else:

   1. PAINT THE SOURCED LINES. Every quoted sentence, every brand claim and
      every outside price range comes out of vr-source.js at runtime and is
      written into a [data-s] slot. None of it is typed in the markup, so
      flipping OFF in the register empties the creator's lines and the page
      still stands on the product facts.

   2. ROUTING. Every CTA points at the product page, on the bundle the closing
      card names. The share-out calculator that used to work out which bundle
      that should be was removed on 2026-08-12 and is parked in ../_parked/.

   THIS PAGE DOES NOT SELL, AND PRINTS NO VEYLA PRICE (Omar, 2026-08-12). It
   routes to the product page, where a figure sits next to the cart that has to
   honour it. So the share-out counts tubes and weeks, never dollars, and the
   closing card states a SAVING rather than a price. The percentage is still
   computed from ../../shared/vy-offer.js at runtime, so a repricing moves it
   without anyone editing this file.

   The only money on this page is hers ($4,000) and the published outside
   ranges. Neither is ours to be wrong about at the checkout.

   ============================================================ */
(() => {
  const O = window.VY_OFFER;
  const S = window.VR_SOURCE;
  if (!O || !S) {
    console.error('[vr] VY_OFFER or VR_SOURCE missing. Serve from the BRAND root:\n' +
      '  cd succession/brands/veyla && python3 -m http.server 8796 --bind 127.0.0.1\n' +
      '  then open /listicles/reasons/');
    return;
  }

  const tubeWord = n => `${n} tube${n === 1 ? '' : 's'}`;

  /* Images the ENGINE applies rather than the markup. On the theme, assets/ is
     flat and these paths do not exist, so the layout emits window.VR_IMG from
     local filename to CDN url and this resolves through it. Off the theme the
     map is absent and the raw relative path already works. */
  const imgUrl = p =>
    (window.VR_IMG && window.VR_IMG[p.split('/').pop()]) || p;

  /* The saving on the DEFAULT bundle, measured against the list price the
     store publishes. Reads the offer module, so it cannot disagree with the
     product page it sends her to. */
  const headlineSave = () => {
    const t = O.TIERS[O.TIER_DEFAULT];
    return Math.round((1 - O.tierTotal(O.TIER_DEFAULT, O.SUB_DEFAULT) / t.compareCents) * 100);
  };
  const esc = s => String(s).replace(/[&<>"]/g, m =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]));
  const $  = s => document.querySelector(s);
  const $$ = s => Array.from(document.querySelectorAll(s));

  /* One tube is four weeks of daily use for one person. It is the same
     constant listicle B and E use, and it is the only assumption the
     share-out makes that is not read from the offer module. */
  const WEEKS_PER_TUBE = 4;

  /* ── 1. paint the sourced lines ───────────────────────── */

  /* Slot naming carries the register, so a reader of the markup can tell whose
     voice a sentence is in without opening this file:

       s:key      SAID     verbatim off the tape, printed in quote marks
       v:key      VOICED   written in her voice from a SAID line (or, with the
                           switch off, the brand's third-person fallback)
       veyla-*    VEYLA    the brand's own claims, only ever inside a .vr-veyla
       out-*      OUTSIDE  published ranges
       byline-*   the persona's name and role, and the disclosure that says the
                  portrait is generated. The disclosure survives the switch;
                  the name and the face do not

     Everything that is not s: or v: is a fact she did not supply, and the
     markup must keep it inside a Veyla panel. paint() enforces that rather
     than trusting it. */
  const SLOTS = {
    'byline-name': S.OFF ? 'Veyla' : S.BYLINE.name,
    'byline-role': S.OFF ? 'Brand editorial' : S.BYLINE.role,
    /* The hero is now the persona holding the product, so the caption has to
       say that it is generated. An unlabelled photograph of a named narrator
       with the tube in her hand is the exact shape of a customer testimonial,
       and this one photographs nobody. With the persona off, the engine serves
       the brand's own campaign model instead and the caption follows. */
    'hero-caption': S.OFF
      ? 'Veyla™ Grow:Turn Ampoule. Veyla’s own campaign image.'
      : `${S.BYLINE.name} with the Veyla™ Grow:Turn Ampoule. Generated image.`,

    'byline-disclosure': S.OFF
      ? 'Written by Veyla. The illustrative account has been switched off in this build.'
      : S.BYLINE.disclosure,

    'veyla-actives': S.VEYLA.actives,
    'veyla-exosome': 'Veyla answers absorption with the carrier rather than the ingredient ' +
      'list. ' + S.VEYLA.exosome.text,
    'veyla-exosome-note': S.VEYLA.exosome.note,
    'veyla-timeline':  S.VEYLA.timeline,
    'veyla-medical':   S.VEYLA.medical,
    'veyla-guarantee': S.VEYLA.guaranteeNote,

    'out-transplant-cost':  S.OUTSIDE.transplant.cost,
    'out-minoxidil-cost':   S.OUTSIDE.minoxidil.cost,
    'out-finasteride-cost': S.OUTSIDE.finasteride.cost,
    'out-salon-cost':       S.OUTSIDE.salon.cost,

  };

  const slotValue = key => {
    if (key.startsWith('s:')) return S.said(key.slice(2));
    if (key.startsWith('v:')) return S.voiced(key.slice(2));
    return SLOTS[key];
  };

  function paint() {
    $$('[data-s]').forEach(el => {
      const key = el.getAttribute('data-s');
      const val = slotValue(key);

      /* A claim she did not supply may not sit in her narrative. This is the
         one rule that makes first person defensible, so it is checked at
         runtime as well as in the harness: a veyla-* or out-* slot outside a
         .vr-veyla panel or the comparison table is a build error, not a
         styling preference. */
      if (/^(veyla|out)-/.test(key) && !el.closest('.vr-veyla, .vr-table, .vr-guar')) {
        console.error('[vr] ' + key + ' is outside a Veyla panel. A brand claim ' +
          'printed inside a first-person narrative reads as hers.');
      }

      if (!val) {
        /* Switched off, or a key that has been retired. Take the whole quote
           block out rather than leaving an empty bordered box. */
        const host = el.closest('.vr-quote') || el;
        if (host.classList.contains('vr-quote')) {
          const live = Array.from(host.querySelectorAll('[data-s]'))
            .some(n => slotValue(n.getAttribute('data-s')));
          if (!live) { host.remove(); return; }
        }
        el.remove();
        return;
      }
      /* Quote marks are added here, once, rather than typed around every quote
         in the markup where one could go missing. Only s: slots get them:
         quote marks on a v: line would present written prose as verbatim. */
      const isQuote = key.startsWith('s:') && !!el.closest('.vr-quote') && el.tagName === 'P';
      el.innerHTML = isQuote ? '&ldquo;' + esc(val) + '&rdquo;' : esc(val);
    });

    /* The saving on the offer this page points at, never the best number
       available somewhere else on the ladder. Computed, never typed: a typed
       figure goes stale in silence the day the ladder moves. */
    /* The portrait is the persona's, so it goes out with her rather than
       lingering above a page written by the brand. */
    const face = $('[data-vr-face]');
    if (face) {
      if (S.OFF) face.remove();
      else { face.src = imgUrl(S.BYLINE.portrait); face.alt = ''; }
    }

    /* Any generated picture OF her goes out with her, rather than standing
       over a page written by the brand. */
    if (S.OFF) $$('[data-vr-persona-img]').forEach(n => n.remove());

    /* Same rule for the hero, which has a brand image to fall back to. */
    const hero = $('[data-vr-hero]');
    if (hero && S.OFF) {
      hero.src = imgUrl('../_shared/img/model-hero.png');
      hero.removeAttribute('width'); hero.removeAttribute('height');
      hero.alt = 'Veyla Grow:Turn Ampoule held beside the face';
    }

    $$('[data-save-pct]').forEach(n => { n.textContent = headlineSave() + '%'; });

    const t = O.TIERS[O.TIER_DEFAULT];
    const sub = $('[data-vr-offer-sub]');
    /* Names the basket the percentage belongs to, and how long it covers, so
       the reader knows what she is being quoted without a number she cannot
       check from here. */
    if (sub) sub.textContent =
      `${t.cardTitle}: ${tubeWord(t.tubes)}, about ${t.tubes * WEEKS_PER_TUBE} weeks of cover for one person.`;
  }

  /* ── 2. where the reader is sent ────────────────────────

     The share-out calculator was removed on 2026-08-12 (Omar). Its markup and
     engine are parked in ../_parked/ rather than deleted, because "in the
     meantime" is not "never". What is left is the shape listicle D uses: the
     page argues, the closing card states a saving, and the product page does
     the selling.

     With no calculator there is no worked-out tier, so every CTA points at the
     bundle the card names, which is the ladder's own default.               */

  const TIER = O.TIER_DEFAULT;

  const destination = () => {
    const id = O.TIERS[TIER] && O.TIERS[TIER].variantId;
    return O.PRODUCT_URL + (id ? '?variant=' + id : '');
  };

  function renderCta() {
    $$('[data-vr-go]').forEach(a => { a.setAttribute('href', destination()); });

    /* The bar follows the reader the whole way down, so it carries no price
       and no percentage. It names the offer and what it covers, both of which
       are facts of the ladder rather than figures. */
    const t = O.TIERS[TIER];
    const st = $('[data-vr-sticky-tier]');
    const ss = $('[data-vr-sticky-sub]');
    if (st) st.textContent = t.cardTitle;
    if (ss) ss.textContent = `${tubeWord(t.tubes)}, about ${t.tubes * WEEKS_PER_TUBE} weeks of cover`;
  }

  const render = renderCta;

  /* ── 3. wiring ────────────────────────────────────────── */

  function wire() {
    /* sticky bar appears once the hero is behind her */
    const sticky = $('[data-vr-sticky]');
    const hero = $('.vr-hero');
    if (sticky && hero && 'IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => {
        sticky.classList.toggle('is-in', !e.isIntersecting);
      }, { rootMargin: '-120px 0px 0px 0px' }).observe(hero);
    }

    /* the offer module re-prices itself when the currency layer lands */
    window.addEventListener('vy:currency', () => { paint(); render(); });
  }

  paint();
  wire();
  render();

  /* exposed for the harness only */
  window.VR = { render, destination, headlineSave, TIER, WEEKS_PER_TUBE };
})();
